import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureCurrentUser, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import { syncShippingOriginFromProfile } from '../util/shippingOrigin';
import {
  HIDDEN_INBOX_TRANSACTION_IDS,
  INBOX_READ_TIMESTAMPS,
  getHiddenInboxTransactionIds,
  getInboxReadTimestamps,
  getLatestIncomingMessage,
  isConversationUnread,
} from '../util/inbox';
import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

////////////////////////////////////////////////////////////////////
// Fetch conversations and compare them with current-user read data //
////////////////////////////////////////////////////////////////////

// Notification page size is the Marketplace API maximum (100 items per page).
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = (
  currentUserOverride,
  { getState, extra: sdk, rejectWithValue }
) => {
  const currentUser = currentUserOverride || getState().user.currentUser;
  const currentUserId = currentUser?.id?.uuid;
  if (!currentUserId) {
    return Promise.resolve({
      saleNotificationsCount: 0,
      orderNotificationsCount: 0,
      unreadTransactionIds: [],
    });
  }

  const paramsForSales = {
    only: 'sale',
    hasMessage: true,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };
  const paramsForOrders = {
    only: 'order',
    hasMessage: true,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  const queryAllTransactions = params => {
    const queryPage = page =>
      sdk.transactions.query({ ...params, page }).then(response => {
        const transactions = response.data.data || [];
        const totalPages = response.data.meta?.totalPages || 1;
        return page < totalPages
          ? queryPage(page + 1).then(nextTransactions => [...transactions, ...nextTransactions])
          : transactions;
      });
    return queryPage(1);
  };

  const messagesForTransactions = (transactions, role) =>
    Promise.all(
      transactions.map(transaction =>
        sdk.messages
          .query({
            transactionId: transaction.id,
            include: ['sender'],
            page: 1,
            perPage: 1,
          })
          .then(response => ({
            transactionId: transaction.id.uuid,
            role,
            messages: denormalisedResponseEntities(response),
          }))
      )
    );

  return Promise.all([queryAllTransactions(paramsForSales), queryAllTransactions(paramsForOrders)])
    .then(([sales, orders]) => {
      return Promise.all([
        messagesForTransactions(sales, 'sale'),
        messagesForTransactions(orders, 'order'),
      ]);
    })
    .then(([sales, orders]) => {
      const hiddenIds = new Set(getHiddenInboxTransactionIds(currentUser));
      const readTimestamps = getInboxReadTimestamps(currentUser);
      const unreadConversations = [...sales, ...orders].filter(({ transactionId, messages }) => {
        const latestIncomingMessage = getLatestIncomingMessage(messages, currentUserId);
        return (
          !hiddenIds.has(transactionId) &&
          isConversationUnread({ transactionId, latestIncomingMessage, readTimestamps })
        );
      });
      const saleNotificationsCount = unreadConversations.filter(c => c.role === 'sale').length;
      const orderNotificationsCount = unreadConversations.filter(c => c.role === 'order').length;
      return {
        saleNotificationsCount,
        orderNotificationsCount,
        unreadTransactionIds: unreadConversations.map(c => c.transactionId),
      };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = currentUser => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk(currentUser)).unwrap();
};

const updateInboxPreferencesPayloadCreator = (
  { transactionId, readAt, hide },
  { getState, dispatch, extra: sdk, rejectWithValue }
) => {
  const currentUser = ensureCurrentUser(getState().user.currentUser);
  const privateData = currentUser?.attributes?.profile?.privateData || {};
  const readTimestamps = getInboxReadTimestamps(currentUser);
  const hiddenIds = getHiddenInboxTransactionIds(currentUser);
  const nextReadTimestamps = readAt
    ? { ...readTimestamps, [transactionId]: new Date(readAt).toISOString() }
    : readTimestamps;
  const nextHiddenIds = hide ? [...new Set([...hiddenIds, transactionId])] : hiddenIds;

  return sdk.currentUser
    .updateProfile(
      {
        privateData: {
          ...privateData,
          [INBOX_READ_TIMESTAMPS]: nextReadTimestamps,
          [HIDDEN_INBOX_TRANSACTION_IDS]: nextHiddenIds,
        },
      },
      { expand: true }
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      return dispatch(fetchCurrentUserNotifications(entities[0])).then(() => entities[0]);
    })
    .catch(error => rejectWithValue(storableError(error)));
};

export const updateInboxPreferencesThunk = createAsyncThunk(
  'user/updateInboxPreferences',
  updateInboxPreferencesPayloadCreator
);

export const markConversationRead = ({ transactionId, readAt }) => dispatch =>
  dispatch(updateInboxPreferencesThunk({ transactionId, readAt })).unwrap();

export const hideInboxConversation = transactionId => dispatch =>
  dispatch(updateInboxPreferencesThunk({ transactionId, hide: true })).unwrap();

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // Seed a missing legacy rating postal code without publishing or replacing private origins.
      syncShippingOriginFromProfile(currentUser, { sdk });

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications(currentUser));
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

// ======================
// Update Wishlist
// ======================

export const WISHLIST_LISTING_IDS = 'wishlistListingIds';

export const updateWishlistThunk = createAsyncThunk(
  'user/updateWishlist',
  ({ listingId, isSaved }, { getState, dispatch, rejectWithValue, extra: sdk }) => {
    const currentUser = ensureCurrentUser(getState().user.currentUser);
    const privateData = currentUser?.attributes?.profile?.privateData || {};
    const savedListingIds = Array.isArray(privateData[WISHLIST_LISTING_IDS])
      ? privateData[WISHLIST_LISTING_IDS]
      : [];
    const wishlistListingIds = isSaved
      ? [...new Set([...savedListingIds, listingId])]
      : savedListingIds.filter(id => id !== listingId);

    return sdk.currentUser
      .updateProfile(
        {
          privateData: {
            ...privateData,
            [WISHLIST_LISTING_IDS]: wishlistListingIds,
          },
        },
        { expand: true }
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(setCurrentUser(entities[0]));
        return wishlistListingIds;
      })
      .catch(error => rejectWithValue(storableError(error)));
  }
);

export const updateWishlist = actionPayload => dispatch => {
  return dispatch(updateWishlistThunk(actionPayload)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserUnreadTransactionIds: [],
    currentUserNotificationCountError: null,
    inboxPreferencesUpdateInProgress: false,
    inboxPreferencesUpdateError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    wishlistUpdateInProgress: false,
    wishlistUpdateError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;
      state.currentUserUnreadTransactionIds = [];

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
        state.currentUserUnreadTransactionIds = action.payload.unreadTransactionIds;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserNotificationCountError = action.payload;
      })
      // updateInboxPreferences
      .addCase(updateInboxPreferencesThunk.pending, state => {
        state.inboxPreferencesUpdateInProgress = true;
        state.inboxPreferencesUpdateError = null;
      })
      .addCase(updateInboxPreferencesThunk.fulfilled, state => {
        state.inboxPreferencesUpdateInProgress = false;
      })
      .addCase(updateInboxPreferencesThunk.rejected, (state, action) => {
        state.inboxPreferencesUpdateInProgress = false;
        state.inboxPreferencesUpdateError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasOrdersError = action.payload;
      })
      // updateWishlist
      .addCase(updateWishlistThunk.pending, state => {
        state.wishlistUpdateInProgress = true;
        state.wishlistUpdateError = null;
      })
      .addCase(updateWishlistThunk.fulfilled, state => {
        state.wishlistUpdateInProgress = false;
      })
      .addCase(updateWishlistThunk.rejected, (state, action) => {
        state.wishlistUpdateInProgress = false;
        state.wishlistUpdateError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const { clearCurrentUser, setCurrentUser, setCurrentUserHasOrders } = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
